import { UserState, CognitoUser, UserData } from './user.types';
import { Dispatch } from 'react';
import Cookies from 'js-cookie';
import {
  LOGIN_USER_ERROR,
  LOGIN_USER_REQUEST,
  LOGIN_USER_SUCCESSFUL,
  GET_USER_BY_ID,
  LOGOUT_USER,
  LOGIN_CLEAN,
  LoginAction,
  LoginErrorAction,
  LoginSuccessfulAction,
  LogoutUserAction,
  UserActions,
  SIGNUP_USER_SUCCESSFUL,
  SignupSuccessfulAction,
  SignupErrorAction,
  SIGNUP_USER_ERROR,
  SignupAction,
  SIGNUP_USER_REQUEST,
  SEND_CODE_REQUEST,
  SEND_CODE_SUCCESSFUL,
  SEND_CODE_ERROR,
  SendCodeAction,
  SendCodeSuccessfulAction,
  SendCodeErrorAction,
  RESET_PASSWORD_REQUEST,
  RESET_PASSWORD_SUCCESSFUL,
  RESET_PASSWORD_ERROR,
  ResetPasswordAction,
  ResetPasswordSuccessfulAction,
  ResetPasswordErrorAction,
  USER_RESPONSE,
} from './user.action.type';
import { RootState } from '..';
import userService from '../../services/user.service';
import authService from '../../services/auth.service';
import errorService from '../../services/error.service';
import { GalaxyToast } from '@dinocloud/galaxy-components-lib';
import { getCurrentLocale } from '../../services/utils.service';

const loginIn = (user: CognitoUser): LoginAction => {
  return {
    type: LOGIN_USER_REQUEST,
    payload: user,
  };
};

const signUp = (): SignupAction => {
  return {
    type: SIGNUP_USER_REQUEST,
  };
};

const cleanLogin = (user: UserState) => {
  return {
    type: LOGIN_CLEAN,
    payload: user,
  };
};

const loginError = (error = ''): LoginErrorAction => {
  return {
    type: LOGIN_USER_ERROR,
    error,
  };
};

const loginSuccessful = (user: CognitoUser): LoginSuccessfulAction => {
  return {
    type: LOGIN_USER_SUCCESSFUL,
    payload: user,
  };
};

const signupSuccessful = (username: string): SignupSuccessfulAction => {
  return {
    type: SIGNUP_USER_SUCCESSFUL,
    payload: username,
  };
};

const signupError = (error = ''): SignupErrorAction => {
  return {
    type: SIGNUP_USER_ERROR,
    error,
  };
};

export const logoutUser = (): LogoutUserAction => {
  authService.cleanStorage();
  return {
    type: LOGOUT_USER,
    payload: null,
  };
};

const userResponse = (userData: UserData): UserActions => {
  return {
    type: USER_RESPONSE,
    payload: userData,
  };
};

export const getUserFromStorage = () => {
  const userData: CognitoUser = JSON.parse(
    Cookies.get('user', { domain: process.env.REACT_APP_COOKIES_DOMAIN })!
  );
  return loginSuccessful(userData);
};

export const cleanLoginMgs = () => {
  return cleanLogin({
    isFetching: false,
    error: null,
  });
};

export const cleanLoginFetching = () => {
  return cleanLogin({
    isFetching: false,
  });
};

export const getCurrentUser = () => {
  const user: CognitoUser = authService.getCurrentUser();
  return {
    type: GET_USER_BY_ID,
    payload: user,
  };
};

export const loginUser = (email: string, password: string) => {
  return async (dispatch: Dispatch<UserActions>, getState: () => RootState) => {
    dispatch(loginIn({}));
    const response: any = await userService.loginUser(email, password);
    if (!!response && response.code) {
      dispatch(loginError(response.message));
      errorService.translateResponse(response);
    } else if (
      !!response &&
      response.message &&
      response.message.includes('newPasswordRequired')
    ) {
      localStorage.setItem('session', await response.cognitoUser.Session);
      dispatch(loginError('newPasswordRequired'));
    } else if (!!response) {
      const idToken = await response.getIdToken().getJwtToken();
      const refreshToken = await response.getRefreshToken().getToken();
      Cookies.set('token', idToken, {
        domain: process.env.REACT_APP_COOKIES_DOMAIN,
      });
      Cookies.set('refreshToken', refreshToken, {
        domain: process.env.REACT_APP_COOKIES_DOMAIN,
      });
      const loginSucessfull: any = await authService.loginSucessfull();
      const decodeToken: any = authService.getTokenDecode();
      const user: CognitoUser = {
        name: decodeToken.name,
        email: decodeToken.email,
        email_verified: decodeToken.email_verified,
      };
      Cookies.set('user', JSON.stringify(user), {
        domain: process.env.REACT_APP_COOKIES_DOMAIN,
      });
      GalaxyToast({
        header:
          getCurrentLocale() === 'es' ? 'Ingreso exitoso' : `Login sucessful`,
        type: 'success',
        message:
          getCurrentLocale() === 'es'
            ? `Bienvenido/a de nuevo ${user.name}`
            : `Welcome ${user.name}`,
      });
      dispatch(loginSuccessful(user));
    }
  };
};

export const signupUser = (
  email: string,
  username: string,
  password: string,
  name: string
) => {
  return async (dispatch: Dispatch<UserActions>, getState: () => RootState) => {
    dispatch(signUp());
    const response: any = await userService.signUpUser(
      email,
      username,
      password,
      name
    );
    if (response && response.code) {
      dispatch(signupError(response.message));
      errorService.translateResponse(response);
      return false;
    } else {
      dispatch(signupSuccessful(username));
      return true;
    }
  };
};

const sendCode = (): SendCodeAction => {
  return {
    type: SEND_CODE_REQUEST,
  };
};

const sendCodeSuccessful = (email: string): SendCodeSuccessfulAction => {
  return {
    type: SEND_CODE_SUCCESSFUL,
    email,
  };
};

const senCodeError = (error = ''): SendCodeErrorAction => {
  return {
    type: SEND_CODE_ERROR,
    error,
  };
};

export const forgotPasswordUser = (email: string) => {
  return async (dispatch: Dispatch<UserActions>, getState: () => RootState) => {
    dispatch(sendCode());
    const response: any = await userService.forgotPassword(email);
    if (response.code) {
      dispatch(senCodeError(response.message));
      errorService.translateResponse(response);
      return false;
    } else {
      dispatch(sendCodeSuccessful(email));
      return true;
    }
  };
};

const resetPassword = (): ResetPasswordAction => {
  return {
    type: RESET_PASSWORD_REQUEST,
  };
};

const resetPasswordSuccessful = (
  email: string
): ResetPasswordSuccessfulAction => {
  return {
    type: RESET_PASSWORD_SUCCESSFUL,
    email,
  };
};

const resetPasswordError = (error = ''): ResetPasswordErrorAction => {
  return {
    type: RESET_PASSWORD_ERROR,
    error,
  };
};

export const confirmPasswordUser = (
  email: string,
  code: string,
  password: string
) => {
  return async (dispatch: Dispatch<UserActions>, getState: () => RootState) => {
    dispatch(resetPassword());
    const response: any = await userService.confirmPassword(
      email,
      code,
      password
    );
    if (response && response.code) {
      dispatch(resetPasswordError(response.message));
      errorService.translateResponse(response);
      return false;
    } else {
      dispatch(resetPasswordSuccessful(email));
      return true;
    }
  };
};

export const newPasswordRequired = (
  email: string,
  newPassword: string,
  name: string
) => {
  return async (dispatch: Dispatch<UserActions>, getState: () => RootState) => {
    dispatch(loginIn({}));
    const response: any = await userService.newPassword(
      email,
      newPassword,
      name
    );
    if (!!response && response.code) {
      dispatch(loginError(response.message));
      errorService.translateResponse(response);
      return false;
    } else if (!!response) {
      const idToken = await response.getIdToken().getJwtToken();
      const refreshToken = await response.getRefreshToken().getToken();
      Cookies.set('token', idToken, {
        domain: process.env.REACT_APP_COOKIES_DOMAIN,
      });
      Cookies.set('refreshToken', refreshToken, {
        domain: process.env.REACT_APP_COOKIES_DOMAIN,
      });
      const decodeToken: any = authService.getTokenDecode();
      const user: CognitoUser = {
        name: decodeToken.name,
        email: decodeToken.email,
        email_verified: decodeToken.email_verified,
      };
      Cookies.set('user', JSON.stringify(user), {
        domain: process.env.REACT_APP_COOKIES_DOMAIN,
      });
      dispatch(loginSuccessful(user));
      return true;
    }
  };
};

export const getUserData = () => {
  return async (dispatch: Dispatch<UserActions>, getState: () => RootState) => {
    let response: any = await userService.getUserData();

    response = await response.data.getUserData;
    dispatch(
      userResponse({
        ...getState().user,
        ...response,
        userData: response,
      })
    );
  };
};
